import i18n from "i18next";
import ICU from "i18next-icu";
import { initReactI18next } from "react-i18next";

import { captureSentryException } from "@/lib/sentry";
import { enGB } from "@/locales/en-GB";
import { getLocalStorageItem } from "@/utils/common";

import { isLanguage, Language } from "./i18n.types";

const syncDocumentLang = (lng: Language) => {
  if (typeof document !== "undefined") {
    document.documentElement.lang = lng;
  }
};

type LocaleFileModule = { default: Record<string, unknown> };

const localeFileLoaders = import.meta.glob<LocaleFileModule>("../locales/*/*.json");

const loadersForLanguage = (lng: Language) => {
  const prefix = `../locales/${lng}/`;
  return Object.entries(localeFileLoaders).filter(([p]) => p.startsWith(prefix));
};

export const mergeLocaleFiles = (
  files: ReadonlyArray<readonly [string, Record<string, unknown>]>
): Record<string, unknown> => {
  const merged: Record<string, unknown> = {};
  // Spread _misc.json first so domain-named files always win on collision.
  const sorted = [...files].sort(([a], [b]) => {
    const aMisc = a.endsWith("/_misc.json");
    const bMisc = b.endsWith("/_misc.json");
    if (aMisc === bMisc) return 0;
    return aMisc ? -1 : 1;
  });
  for (const [filePath, content] of sorted) {
    const name =
      filePath
        .split("/")
        .pop()
        ?.replace(/\.json$/, "") ?? "";
    if (name === "_misc") {
      Object.assign(merged, content);
    } else {
      merged[name] = content;
    }
  }
  return merged;
};

const storedLanguage = getLocalStorageItem<string | null | undefined>("language") ?? "en-GB";
const initialLanguage: Language = isLanguage(storedLanguage) ? storedLanguage : "en-GB";

i18n
  .use(ICU)
  .use(initReactI18next)
  .init({
    resources: { "en-GB": { translation: enGB } },
    lng: "en-GB",
    fallbackLng: "en-GB",
    interpolation: {
      escapeValue: false,
    },
  });

syncDocumentLang("en-GB");

const loadedLanguages = new Set<Language>(["en-GB"]);

// Per-language cache of the merged BASE bundle (no terminology overrides applied).
// Needed so the terminology engine can RESET to base wording before re-applying a
// (possibly different) override set on a platform/language switch. en-GB's base is
// the `enGB` object already used to bootstrap i18n above.
const baseMergedByLanguage = new Map<Language, Record<string, unknown>>([["en-GB", enGB]]);

/**
 * Returns the merged BASE bundle for a language (default term wording, no
 * terminology overrides). Falls back to en-GB if the language hasn't been
 * loaded yet. Call `loadLanguage(lng)` first to guarantee a non-fallback result.
 */
export const getBaseMerged = (lng: Language): Record<string, unknown> =>
  baseMergedByLanguage.get(lng) ?? enGB;

/** Languages whose base bundle has been loaded (and is therefore safe to reset to). */
export const getLoadedBaseLanguages = (): Language[] => [...baseMergedByLanguage.keys()];

export const loadLanguage = async (lng: Language): Promise<void> => {
  if (loadedLanguages.has(lng)) return;
  const loaders = loadersForLanguage(lng);
  if (!loaders.length) throw new Error(`No locale files registered for "${lng}"`);
  const files = await Promise.all(
    loaders.map(async ([filePath, load]) => {
      const mod = await load();
      return [filePath, mod.default] as const;
    })
  );
  const merged = mergeLocaleFiles(files);
  baseMergedByLanguage.set(lng, merged);
  i18n.addResourceBundle(lng, "translation", merged, true, true);
  loadedLanguages.add(lng);
};

let isLoading = initialLanguage !== "en-GB";
const subscribers = new Set<() => void>();

const notify = () => {
  for (const s of subscribers) s();
};

export const setI18nLoading = (value: boolean) => {
  if (isLoading === value) return;
  isLoading = value;
  notify();
};

export const getI18nLoading = () => isLoading;

export const subscribeI18nLoading = (cb: () => void) => {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
};

void (async () => {
  if (initialLanguage === "en-GB") return;
  try {
    await loadLanguage(initialLanguage);
    await i18n.changeLanguage(initialLanguage);
    syncDocumentLang(initialLanguage);
  } catch (err) {
    captureSentryException(err, {
      tags: { source: "i18n.loadInitialLocale" },
      extra: { locale: initialLanguage },
    });
  } finally {
    setI18nLoading(false);
  }
})();

export { i18n, syncDocumentLang };
